import { useState } from "react";

import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { GridPattern } from "@/components/ui/grid-pattern";

import { forgotPassword } from "../api/authApi";

function Spinner() {
  return (
    <svg
      className="mr-2 h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

const ForgotPasswordPage = () => {
  const { t } = useTranslation("common");

  const [email, setEmail] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setError(null);

    setIsLoading(true);

    try {
      await forgotPassword(email);

      setIsSuccess(true);
    } catch {
      setError(t("FORGOT_PASSWORD.errorConfiguring"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
      <GridPattern
        width={40}
        height={40}
        className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] [stroke:var(--primary)] [stroke-opacity:0.2]"
      />

      <Card className="relative z-10 w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/logo.png" alt="Eriol" className="h-12 w-12 mx-auto mb-6" />
          <CardTitle className="text-2xl">
            {t("FORGOT_PASSWORD.title")}
          </CardTitle>
          <CardDescription>{t("FORGOT_PASSWORD.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 text-destructive p-3 mb-4 text-center text-sm">
              {error}
            </div>
          )}

          {isSuccess ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 text-green-400">
                <svg
                  className="h-7 w-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("FORGOT_PASSWORD.successMessage")}
              </p>
              <Link
                to="/login"
                className="mt-2 text-sm font-medium text-primary hover:underline"
              >
                {t("FORGOT_PASSWORD.backToLogin")}
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("LOGIN.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@eriol.io"
                    autoComplete="email"
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Spinner />}
                  {t("FORGOT_PASSWORD.submit")}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-6">
                <Link to="/login" className="text-primary hover:underline">
                  {t("FORGOT_PASSWORD.backToLogin")}
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
